import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Button, Container, Paper, Dialog, DialogContent, DialogActions, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import ListeningTestUI from '../components/IELTS/ListeningTestUI';
import TestModeModal from '../components/IELTS/TestModeModal';
import { getBandScore } from '../components/IELTS/utils/scoreHelpers';
import { getCorrectIncorrectCounts } from '../components/IELTS/IELTSBandScore';
import { loadIeltsDraftStateFromBrowser } from '../components/IELTS/utils/ieltsDraftStorage';
import { isListeningSectionRenderable } from '../components/IELTS/utils/questionHelpers';
import { IELTS_SECTION_DURATIONS } from './ieltsFullTestSections';

const LISTENING_DEFAULT_TIME = IELTS_SECTION_DURATIONS.Listening;
const LISTENING_CONTENT_ERROR = 'Listening test content is unavailable. Please return to the dashboard and try again.';
const LISTENING_SIMULATION_ERROR = 'Listening simulation requires four available sections.';

const PageContainer = styled(Container)(({ theme }) => ({
  padding: '24px',
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
}));

const InstructionsContainer = styled(Paper)(({ theme }) => ({
  padding: '24px',
  marginBottom: '24px',
  borderRadius: '12px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.05)',
}));

const StartButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#996CFE',
  color: 'white',
  padding: '10px 24px',
  fontSize: '16px',
  '&:hover': {
    backgroundColor: '#7C4DFF',
  },
}));

const ResultDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    maxWidth: '600px',
    width: '100%',
  },
}));

const ListeningTestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [testStarted, setTestStarted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [modeModalOpen, setModeModalOpen] = useState(true);
  const [selectedMode, setSelectedMode] = useState(null); // 'practice' or 'simulation'
  const [selectedSectionCount, setSelectedSectionCount] = useState(4); // default to 4 for simulation
  const [selectedTimeLimit, setSelectedTimeLimit] = useState(LISTENING_DEFAULT_TIME);
  const [selectedSectionIndices, setSelectedSectionIndices] = useState([0,1,2,3]); // default to all
  const [startError, setStartError] = useState(null);
  
  const testDuration = selectedTimeLimit; // Use selected time limit
  const testData = location.state?.testData;
  const resumeDraft = Boolean(location.state?.resumeDraft);
  const hasValidSimulationSections = testData?.sections?.length === 4
    && testData.sections.every(isListeningSectionRenderable);

  useEffect(() => {
    if (!resumeDraft || !testData || !Array.isArray(testData.sections) || testData.sections.length === 0) return;
    const draft = loadIeltsDraftStateFromBrowser({ scope: 'sectional', section: 'listening', testId: testData.id });
    const savedMode = draft?.currentStep?.mode;
    if (!savedMode) return;
    if (savedMode === 'simulation' && !hasValidSimulationSections) {
      setStartError(LISTENING_SIMULATION_ERROR);
      return;
    }
    if (savedMode === 'simulation') {
      setSelectedSectionIndices([0, 1, 2, 3]);
      setSelectedSectionCount(4);
    } else {
      const savedSectionIndices = Array.isArray(draft?.currentStep?.selectedSectionIndices)
        ? draft.currentStep.selectedSectionIndices
        : testData.sections.map((_, index) => index);
      const restoredSectionIndices = [...new Set(savedSectionIndices
        .map(Number)
        .filter((index) => Number.isInteger(index) && index >= 0 && index < testData.sections.length))];
      const safeSectionIndices = restoredSectionIndices.length
        ? restoredSectionIndices
        : testData.sections.map((_, index) => index);
      if (safeSectionIndices.some((index) => !isListeningSectionRenderable(testData.sections[index]))) {
        setStartError(LISTENING_CONTENT_ERROR);
        return;
      }
      setSelectedSectionIndices(safeSectionIndices);
      setSelectedSectionCount(safeSectionIndices.length);
    }
    setSelectedMode(savedMode);
    if (draft?.timeState?.timeRemaining && Number.isFinite(Number(draft.timeState.timeRemaining))) {
      setSelectedTimeLimit(Math.ceil(Number(draft.timeState.timeRemaining) / 60));
    }
    setModeModalOpen(false);
    setTestStarted(true);
  }, [hasValidSimulationSections, resumeDraft, testData]);
  
  // Handle modal callbacks
  const handleStartPracticeMode = (selectedParts = [1,2,3,4], timeLimit = LISTENING_DEFAULT_TIME) => {
    const sectionIndices = selectedParts.map((part) => part - 1);
    if (!sectionIndices.length
      || sectionIndices.some((index) => !isListeningSectionRenderable(testData?.sections?.[index]))) {
      setStartError(LISTENING_CONTENT_ERROR);
      return;
    }
    setStartError(null);
    setSelectedMode('practice');
    setSelectedSectionIndices(sectionIndices);
    setSelectedSectionCount(selectedParts.length);
    setSelectedTimeLimit(timeLimit);
    setModeModalOpen(false);
    setTestStarted(true);
  };
  const handleStartSimulationMode = () => {
    if (!hasValidSimulationSections) {
      setStartError(LISTENING_SIMULATION_ERROR);
      return;
    }
    setStartError(null);
    setSelectedMode('simulation');
    setSelectedSectionIndices([0, 1, 2, 3]);
    setSelectedSectionCount(4);
    setSelectedTimeLimit(LISTENING_DEFAULT_TIME);
    setModeModalOpen(false);
    setTestStarted(true);
  };
  
  const handleTestComplete = (results) => {
    if (!results?.response) return;
    setTestResults(results);
  };
  
  const handleCloseResults = () => {
    setShowResults(false);
    navigate('/dashboard');
  };
  
  if (!testData) {
    return <Typography variant="h6" color="error">No test data found. Please return to the dashboard and try again.</Typography>;
  }

  if (!Array.isArray(testData.sections) || testData.sections.length === 0) {
    return <Typography variant="h6" color="error">{LISTENING_CONTENT_ERROR}</Typography>;
  }

  if (!testStarted) {
    return (
      <>
        <TestModeModal
          open={modeModalOpen}
          onClose={() => {
            setModeModalOpen(false);
            navigate('/ielts-dashboard');
          }}
          onStartPracticeMode={handleStartPracticeMode}
          onStartSimulationMode={handleStartSimulationMode}
          testCategoryName="Listening"
          defaultTime={LISTENING_DEFAULT_TIME}
          timeOptions={[LISTENING_DEFAULT_TIME, 25, 20, 'Unlimited']}
        />
        {startError && <Typography color="error">{startError}</Typography>}
      </>
    );
  }

  const resultResponse = testResults?.response;
  const resultScore = getBandScore(testResults);
  const resultCounts = resultResponse
    ? getCorrectIncorrectCounts(resultResponse, resultResponse.analytics, resultResponse.totalQuestions)
    : null;
  const resultCorrectAnswers = typeof resultResponse?.correctCount === 'number'
    ? resultResponse.correctCount
    : resultCounts?.correct || 0;
  const resultTotalQuestions = resultResponse?.totalQuestions
    ?? (resultCounts ? resultCounts.correct + resultCounts.incorrect : 0);
  
  return (
    <>
      <ListeningTestUI 
        mode={selectedMode}
        timeLimit={testDuration} 
        onComplete={handleTestComplete} 
        sections={testData.sections}
        totalPartCount={testData.sections.length}
        selectedSectionIndices={selectedSectionIndices}
        audioUrl={testData.audio_url}
        testId={testData.id}
        restoreDraft={resumeDraft}
      />
      
      <ResultDialog open={showResults} onClose={handleCloseResults}>
        <DialogContent>
          <Typography variant="h5" sx={{ color: '#996CFE', mb: 3, textAlign: 'center' }}>
            Test Results
          </Typography>
          
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 3, color: '#333', fontWeight: 'bold' }}>
            {resultScore !== null ? resultScore.toFixed(1) : 'N/A'}
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, textAlign: 'center' }}>
            You answered {resultCorrectAnswers} out of {resultTotalQuestions} questions correctly.
          </Typography>
          
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
            This is a practice test. Your score is an estimate based on the number of correct answers.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button 
            variant="contained" 
            onClick={handleCloseResults}
            sx={{ 
              backgroundColor: '#996CFE', 
              color: 'white',
              '&:hover': { backgroundColor: '#7C4DFF' } 
            }}
          >
            Return to Dashboard
          </Button>
        </DialogActions>
      </ResultDialog>
    </>
  );
};

export default ListeningTestPage;
